import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import BottomNav from "@/components/BottomNav";
import CharacterCard from "@/components/CharacterCard";
import AgeVerifyModal from "@/components/AgeVerifyModal";
import InstallPWA from "@/components/InstallPWA";
import NotificationsBell from "@/components/NotificationsBell";
import { Search, Inbox, Sparkles } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";

export default function Discover() {
  const { user, updateProfile, refresh } = useAuth();
  const nav = useNavigate();
  const [chars, setChars] = useState([]);
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("all"); // all | trending | following
  const [loading, setLoading] = useState(true);
  const [verifyOpen, setVerifyOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const nsfwOn = !!user?.nsfw_enabled;
  const canNsfw = !!user?.nsfw_verified;

  useEffect(() => {
    if (user?.is_admin) {
      api.get("/admin/feedback/unread-count")
        .then(({ data }) => setUnread(data.unread || 0))
        .catch(() => {});
    }
  }, [user?.is_admin]);

  async function toggleNsfw(v) {
    if (v && !canNsfw) {
      setVerifyOpen(true);
      return;
    }
    try {
      await updateProfile({ nsfw_enabled: !!v });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not toggle NSFW.");
    }
  }

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api
      .get("/characters/discover", { params: { nsfw: nsfwOn, q, tab } })
      .then(({ data }) => alive && setChars(data.characters))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [nsfwOn, q, tab]);

  return (
    <div className="mx-auto max-w-2xl px-4 pb-32 pt-safe-6 sm:pt-safe-6">
      <header className="mb-5 flex items-center justify-between">
        <div>
          <div className="font-display text-xs uppercase tracking-[0.35em] text-violet-400/80">Immersive</div>
          <h1 className="font-display text-3xl leading-tight text-white">Discover</h1>
        </div>
        <div className="flex items-center gap-2">
          {user?.is_admin && (
            <button
              data-testid="discover-admin-inbox-btn"
              onClick={() => nav("/admin/feedback")}
              className="relative flex h-10 w-10 items-center justify-center rounded-full bg-white/5 text-white/70 hover:bg-white/10"
              aria-label="Admin inbox"
            >
              <Inbox size={18} />
              {unread > 0 && (
                <span
                  data-testid="discover-unread-badge"
                  className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-bold text-white"
                >
                  {unread > 9 ? "9+" : unread}
                </span>
              )}
            </button>
          )}
          <NotificationsBell />
        </div>
      </header>

      <InstallPWA />

      <div className="relative mb-4">
        <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-500" />
        <input
          data-testid="discover-search-input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by name, creator, tag…"
          className="w-full rounded-full border border-white/10 bg-white/5 py-3 pl-11 pr-4 text-sm text-white placeholder:text-neutral-500 focus:border-violet-500/50 focus:outline-none"
        />
      </div>

      <div className="mb-5 flex items-center gap-2 overflow-x-auto pb-1">
        {[
          { key: "all", label: "For You" },
          { key: "trending", label: "Trending" },
          { key: "following", label: "Following" },
        ].map((t) => (
          <button
            key={t.key}
            data-testid={`discover-tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`shrink-0 rounded-full px-4 py-2 text-sm transition ${
              tab === t.key
                ? "bg-white text-neutral-900"
                : "border border-white/10 bg-transparent text-neutral-300 hover:bg-white/5"
            }`}
          >
            {t.label}
          </button>
        ))}

        <div className="ml-auto flex shrink-0 items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1.5">
          <span className="text-[11px] uppercase tracking-widest text-neutral-400">NSFW</span>
          <Switch
            data-testid="discover-nsfw-toggle"
            checked={nsfwOn}
            onCheckedChange={toggleNsfw}
          />
        </div>
      </div>

      {user && !user.is_plus && !user.preview_mode && (
        <Link
          to="/upgrade"
          data-testid="discover-upgrade-banner"
          className="mb-5 flex items-center gap-3 rounded-2xl border border-violet-500/30 bg-gradient-to-r from-violet-500/10 to-fuchsia-500/10 p-3 hover:from-violet-500/15 hover:to-fuchsia-500/15"
        >
          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-violet-500/20 text-violet-300">
            <Sparkles size={16} />
          </span>
          <div className="flex-1">
            <div className="text-sm text-white">Unlock <em className="not-italic text-violet-300">Immersive+</em></div>
            <div className="text-[11px] text-neutral-400">Unlimited chats & characters. $14.99/mo</div>
          </div>
          <span className="text-violet-300 text-sm">→</span>
        </Link>
      )}

      {loading ? (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="aspect-[3/4] animate-pulse rounded-2xl bg-white/[0.03]" />
          ))}
        </div>
      ) : chars.length === 0 ? (
        <div data-testid="discover-empty" className="mt-12 text-center text-neutral-500">
          {tab === "following"
            ? <>You aren't following anyone yet. Open any character and tap their creator to explore.</>
            : "No characters found."}
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3" data-testid="discover-grid">
          {chars.map((c, i) => (
            <CharacterCard
              key={c.id}
              character={c}
              testIdPrefix="discover"
              onEngagementChange={(patch) => setChars((prev) => prev.map((x, j) => j === i ? { ...x, ...patch } : x))}
            />
          ))}
        </div>
      )}

      <BottomNav />
      <AgeVerifyModal
        open={verifyOpen}
        onOpenChange={setVerifyOpen}
        onVerified={async () => { await refresh(); await updateProfile({ nsfw_enabled: true }); }}
      />
    </div>
  );
}
